/**
 * Permission Status Indicator
 * Visual indicator showing current permission status
 */

import React from 'react'
import { Box, Chip, Tooltip } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import VideocamIcon from '@mui/icons-material/Videocam'
import MicIcon from '@mui/icons-material/Mic'
import VideocamOffIcon from '@mui/icons-material/VideocamOff'
import MicOffIcon from '@mui/icons-material/MicOff'
import { PERMISSION_STATES } from 'src/services/mediaPermissions'

export const PermissionStatusIndicator = ({
  cameraPermission,
  microphonePermission,
  showLabels = true,
  size = 'medium',
  onClick = null,
}) => {
  const getStatusColor = (state) => {
    switch (state) {
      case PERMISSION_STATES.GRANTED:
        return 'success'
      case PERMISSION_STATES.DENIED:
        return 'error'
      case PERMISSION_STATES.PROMPT:
        return 'warning'
      default:
        return 'default'
    }
  }

  const getStatusIcon = (permission, state) => {
    if (state === PERMISSION_STATES.GRANTED) {
      return permission === 'camera' ? <VideocamIcon /> : <MicIcon />
    } else if (state === PERMISSION_STATES.DENIED) {
      return permission === 'camera' ? <VideocamOffIcon /> : <MicOffIcon />
    } else if (state === PERMISSION_STATES.PROMPT) {
      return <HelpOutlineIcon />
    }
    return null
  }

  const getStatusLabel = (permission, state) => {
    const permissionName = permission === 'camera' ? 'Camera' : 'Microphone'
    switch (state) {
      case PERMISSION_STATES.GRANTED:
        return `${permissionName}: Enabled`
      case PERMISSION_STATES.DENIED:
        return `${permissionName}: Blocked`
      case PERMISSION_STATES.PROMPT:
        return `${permissionName}: Not Set`
      default:
        return `${permissionName}: Unknown`
    }
  }

  const getTooltip = (permission, state) => {
    const permissionName = permission === 'camera' ? 'camera' : 'microphone'
    switch (state) {
      case PERMISSION_STATES.GRANTED:
        return `Your ${permissionName} is enabled and ready to use`
      case PERMISSION_STATES.DENIED:
        return `Your ${permissionName} is blocked. Click to enable it.`
      case PERMISSION_STATES.PROMPT:
        return `${permissionName} permission not yet requested`
      default:
        return `Unable to determine ${permissionName} status`
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        alignItems: 'center',
      }}
    >
      {cameraPermission && (
        <Tooltip title={getTooltip('camera', cameraPermission)} arrow>
          <Chip
            icon={getStatusIcon('camera', cameraPermission)}
            label={showLabels ? getStatusLabel('camera', cameraPermission) : ''}
            color={getStatusColor(cameraPermission)}
            size={size}
            onClick={
              onClick && cameraPermission === PERMISSION_STATES.DENIED
                ? () => onClick('camera')
                : undefined
            }
            sx={{
              cursor:
                onClick && cameraPermission === PERMISSION_STATES.DENIED
                  ? 'pointer'
                  : 'default',
            }}
          />
        </Tooltip>
      )}

      {microphonePermission && (
        <Tooltip title={getTooltip('microphone', microphonePermission)} arrow>
          <Chip
            icon={getStatusIcon('microphone', microphonePermission)}
            label={showLabels ? getStatusLabel('microphone', microphonePermission) : ''}
            color={getStatusColor(microphonePermission)}
            size={size}
            onClick={
              onClick && microphonePermission === PERMISSION_STATES.DENIED
                ? () => onClick('microphone')
                : undefined
            }
            sx={{
              cursor:
                onClick && microphonePermission === PERMISSION_STATES.DENIED
                  ? 'pointer'
                  : 'default',
            }}
          />
        </Tooltip>
      )}
    </Box>
  )
}

export default PermissionStatusIndicator


















